import { mappedColors } from '@pixee/design-system'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './correlation-canvas.css.ts'
import {
  ERRORED_COLOR_TOKEN,
  GHOST_COLOR_TOKEN,
  HELD_COLOR_TOKEN,
  HYPOTHESIS_COLOR_TOKEN,
  kindLabel,
  NEVER_INDEXED_COLOR_TOKEN,
  resolveCssColor,
  toolColorToken,
  toolDisplayName,
} from './exploit-chains-palette.ts'
import { countVerdicts, type Hypothesis, isVerifying, type RunState } from './exploit-chains-run.ts'

/**
 * The correlation run as one picture: every finding is a point on a ring, grouped by the tool
 * that found it; every hypothesis is a chord through the findings it would connect. A chord is
 * dashed while it is only proposed, pulses while it is being verified, then fades to a ghost if
 * it did not hold or lights up solid if it did. An empty run therefore ends as a ring of findings
 * with faint ghost chords across it — what was tried stays visible.
 */

export const CorrelationCanvasSizeSchema = z.enum(['large', 'small'])
export type CorrelationCanvasSize = z.infer<typeof CorrelationCanvasSizeSchema>

// Beyond this many points the ring is a smear; larger dispatches show every finding a hypothesis
// references plus an even sample of the rest, so the tool proportions still read.
const MAX_POINTS = 360

// Angular gap between tool groups, in radians.
const GROUP_GAP = 0.09

const RING_RADIUS_RATIO = 0.4

// Chords bow toward the center by this fraction so they stay inside the ring.
const CHORD_BOW = 0.35

const DASH_SPEED_PX_PER_MS = 0.04

const TWO_PI = Math.PI * 2

type Point = { x: number; y: number; toolIndex: number }

type Layout = {
  points: Map<string, Point>
  arcs: { start: number; end: number; toolIndex: number }[]
  center: { x: number; y: number }
  radius: number
}

const sampleEvenly = <T,>(items: readonly T[], count: number): T[] => {
  if (items.length <= count) {
    return [...items]
  }
  const step = items.length / count
  return Array.from({ length: count }, (_, index) => items[Math.floor(index * step)]).filter(
    (item): item is T => item !== undefined
  )
}

/** Which finding ids get a point: everything a hypothesis cites, then an even sample up to the cap. */
const visibleFindingIds = (run: RunState): Set<string> => {
  const referenced = new Set(run.hypotheses.flatMap(hypothesis => hypothesis.findingIds))
  const all = (run.tools ?? []).flatMap(tool => tool.findingIds)
  if (all.length <= MAX_POINTS) {
    return new Set(all)
  }
  const rest = all.filter(id => !referenced.has(id))
  const sampled = sampleEvenly(rest, Math.max(MAX_POINTS - referenced.size, 0))
  return new Set([...referenced, ...sampled])
}

const layoutRing = (run: RunState, width: number, height: number): Layout => {
  const center = { x: width / 2, y: height / 2 }
  const radius = Math.min(width, height) * RING_RADIUS_RATIO
  const tools = run.tools ?? []
  const visible = visibleFindingIds(run)
  const groups = tools.map((tool, toolIndex) => ({
    toolIndex,
    ids: tool.findingIds.filter(id => visible.has(id)),
  }))
  const total = groups.reduce((sum, group) => sum + group.ids.length, 0)
  const span = TWO_PI - GROUP_GAP * Math.max(groups.length, 1)
  const points = new Map<string, Point>()
  const arcs: Layout['arcs'] = []
  let angle = -Math.PI / 2 + GROUP_GAP / 2
  for (const group of groups) {
    const share = total === 0 ? span / groups.length : (span * group.ids.length) / total
    group.ids.forEach((id, index) => {
      const theta = angle + (share * (index + 0.5)) / group.ids.length
      points.set(id, {
        x: center.x + radius * Math.cos(theta),
        y: center.y + radius * Math.sin(theta),
        toolIndex: group.toolIndex,
      })
    })
    arcs.push({ start: angle, end: angle + share, toolIndex: group.toolIndex })
    angle += share + GROUP_GAP
  }
  return { points, arcs, center, radius }
}

// Hit radius for hovering a point, in CSS pixels.
const HOVER_RADIUS = 14

/** The finding whose point is under the pointer, if any. */
const findingAt = (
  canvas: HTMLCanvasElement | null,
  layout: Layout | undefined,
  event: React.MouseEvent<HTMLCanvasElement>
): string | undefined => {
  if (!canvas || !layout) {
    return undefined
  }
  const bounds = canvas.getBoundingClientRect()
  const x = event.clientX - bounds.left
  const y = event.clientY - bounds.top
  let best: string | undefined
  let bestDistance = HOVER_RADIUS ** 2
  for (const [id, point] of layout.points) {
    const distance = (point.x - x) ** 2 + (point.y - y) ** 2
    if (distance < bestDistance) {
      bestDistance = distance
      best = id
    }
  }
  return best
}

type ChordStyle = {
  color: string
  width: number
  alpha: number
  dash?: number[]
  dashOffset?: number
  glow?: boolean
}

const chordStyleFor = ({
  hypothesis,
  isDimmed,
  isInvolved,
  elapsedMs,
  scale,
  palette,
}: {
  hypothesis: Hypothesis
  isDimmed: boolean
  isInvolved: boolean
  elapsedMs: number
  scale: number
  palette: Record<'hypothesis' | 'held' | 'ghost' | 'errored', string>
}): ChordStyle => {
  switch (hypothesis.verdict) {
    case 'pending':
      return { color: palette.hypothesis, width: 1.2 * scale + 0.4, alpha: isDimmed ? 0.12 : 0.75, dash: [5, 5] }
    case 'verifying':
      return {
        color: palette.hypothesis,
        width: 2 * scale + 0.5,
        alpha: isDimmed ? 0.15 : 1,
        dash: [6, 4],
        dashOffset: -((elapsedMs * DASH_SPEED_PX_PER_MS) % 20),
        glow: !isDimmed,
      }
    case 'rejected':
      return { color: palette.ghost, width: 1.1, alpha: isInvolved ? 0.9 : isDimmed ? 0.08 : 0.3 }
    case 'confirmed':
      return { color: palette.held, width: 2.6 * scale + 0.6, alpha: isDimmed ? 0.3 : 1, glow: true }
    case 'errored':
      return { color: palette.errored, width: 1.1, alpha: isDimmed ? 0.15 : 0.5, dash: [2, 4] }
  }
}

const centerCopy = (run: RunState): { headline: string; caption: string } => {
  const verdicts = countVerdicts(run)
  const decided = verdicts.confirmed + verdicts.rejected + verdicts.errored
  const total = run.totalFindings ?? 0
  if (run.status === 'completed') {
    return {
      headline: verdicts.confirmed > 0 ? `${verdicts.confirmed} held` : 'No chain held',
      caption: `${run.hypotheses.length} ${pluralize(run.hypotheses.length, 'hypothesis', 'hypotheses')} tested · ${run.indexed ?? total} of ${total} findings indexed`,
    }
  }
  if (run.hypotheses.length > 0) {
    return {
      headline: `${decided} / ${run.hypotheses.length}`,
      caption: verdicts.confirmed > 0 ? `verified · ${verdicts.confirmed} held so far` : 'hypotheses verified',
    }
  }
  const finishedLenses = run.lenses.filter(lens => lens.state === 'done')
  if (run.lenses.length > 0) {
    const proposed = finishedLenses.reduce((sum, lens) => sum + lens.proposed, 0)
    return {
      headline: finishedLenses.length > 0 ? `${proposed}` : '…',
      caption:
        finishedLenses.length > 0
          ? `candidates proposed by ${finishedLenses.length} of ${run.lenses.length} lenses`
          : `${run.lenses.length} lenses reading the findings`,
    }
  }
  if (run.indexed !== undefined) {
    return { headline: `${run.indexed}`, caption: `findings indexed · ${Math.max(total - run.indexed, 0)} over budget` }
  }
  if (run.tools) {
    return {
      headline: `${total}`,
      caption: `findings from ${run.tools.length} ${pluralize(run.tools.length, 'tool', 'tools')}`,
    }
  }
  return { headline: '', caption: 'Waiting for findings…' }
}

export type CorrelationCanvasProps = {
  run: RunState
  size?: CorrelationCanvasSize
  /** A hypothesis id (from the log) or finding id to emphasise; everything else recedes. */
  highlightedId?: string
  onHighlightFinding?: (findingId: string | undefined) => void
  className?: string
}

export const CorrelationCanvas: React.FC<CorrelationCanvasProps> = ({
  run,
  size = 'large',
  highlightedId,
  onHighlightFinding,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [hoveredFindingId, setHoveredFindingId] = useState<string>()
  const layoutRef = useRef<Layout | undefined>(undefined)
  const activeHighlight = hoveredFindingId ?? highlightedId
  const isAnimating = isVerifying(run)
  const verdicts = countVerdicts(run)
  const label = useMemo(() => {
    const tools = run.tools?.length ?? 0
    return `Correlation canvas: ${run.totalFindings ?? 0} findings from ${tools} ${pluralize(tools, 'tool', 'tools')}, ${run.hypotheses.length} ${pluralize(run.hypotheses.length, 'hypothesis', 'hypotheses')}, ${verdicts.confirmed} held`
  }, [run.totalFindings, run.tools?.length, run.hypotheses.length, verdicts.confirmed])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }
    const context = canvas.getContext('2d')
    if (!context) {
      return
    }
    const scale = size === 'small' ? 0.5 : 1
    // Resolved per draw, not per effect: a theme switch changes the CSS variables without touching
    // any dependency of this effect, and getComputedStyle reads cached values so the cost is small.
    const resolvePalette = () => ({
      hypothesis: resolveCssColor(HYPOTHESIS_COLOR_TOKEN, canvas),
      held: resolveCssColor(HELD_COLOR_TOKEN, canvas),
      ghost: resolveCssColor(GHOST_COLOR_TOKEN, canvas),
      errored: resolveCssColor(ERRORED_COLOR_TOKEN, canvas),
      neverIndexed: resolveCssColor(NEVER_INDEXED_COLOR_TOKEN, canvas),
      ink: resolveCssColor(mappedColors.neutral.neutral10, canvas),
      inkMuted: resolveCssColor(mappedColors.neutral.neutral30, canvas),
      ringDim: resolveCssColor(mappedColors.neutral.neutral60, canvas),
      tools: (run.tools ?? []).map((_, index) => resolveCssColor(toolColorToken(index), canvas)),
    })
    const dropped = new Set(run.droppedIds)
    const highlightedHypothesis = run.hypotheses.find(hypothesis => hypothesis.id === activeHighlight)
    const emphasisedFindings = new Set(
      highlightedHypothesis ? highlightedHypothesis.findingIds : activeHighlight ? [activeHighlight] : []
    )
    const startedAt = performance.now()
    let animationFrameId: number | undefined
    // Set on cleanup so a frame this effect scheduled before it was torn down draws nothing and
    // schedules nothing: without it, a resize during animation could leave a second rAF loop racing
    // the new effect's loop on the same canvas.
    let isCanceled = false
    let width = 0
    let height = 0

    const resize = () => {
      const devicePixelRatio = window.devicePixelRatio || 1
      const bounds = canvas.getBoundingClientRect()
      width = bounds.width
      height = bounds.height
      canvas.width = Math.max(1, Math.round(width * devicePixelRatio))
      canvas.height = Math.max(1, Math.round(height * devicePixelRatio))
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0)
      layoutRef.current = layoutRing(run, width, height)
    }

    const drawChord = (hypothesis: Hypothesis, layout: Layout, style: ChordStyle) => {
      const points = hypothesis.findingIds.map(id => layout.points.get(id)).filter((point): point is Point => !!point)
      if (points.length < 2) {
        return
      }
      const [first, ...rest] = points
      if (!first) {
        return
      }
      context.beginPath()
      context.moveTo(first.x, first.y)
      let previous = first
      for (const next of rest) {
        const midX = (previous.x + next.x) / 2
        const midY = (previous.y + next.y) / 2
        context.quadraticCurveTo(
          layout.center.x + (midX - layout.center.x) * CHORD_BOW,
          layout.center.y + (midY - layout.center.y) * CHORD_BOW,
          next.x,
          next.y
        )
        previous = next
      }
      context.setLineDash(style.dash ?? [])
      context.lineDashOffset = style.dashOffset ?? 0
      context.lineWidth = style.width
      context.strokeStyle = style.color
      context.globalAlpha = style.alpha
      context.shadowBlur = style.glow ? 14 : 0
      context.shadowColor = style.color
      context.stroke()
      context.shadowBlur = 0
      context.globalAlpha = 1
      context.setLineDash([])
    }

    const draw = (now: number) => {
      const layout = layoutRef.current
      if (isCanceled || !layout) {
        return
      }
      context.clearRect(0, 0, width, height)
      const palette = resolvePalette()
      const toolColors = palette.tools
      const hasInputs = !!run.tools
      const isHighlighting = emphasisedFindings.size > 0

      context.lineWidth = 1
      for (const arc of layout.arcs) {
        context.beginPath()
        context.strokeStyle = toolColors[arc.toolIndex] ?? palette.ringDim
        context.globalAlpha = hasInputs ? 0.35 : 0.1
        context.arc(layout.center.x, layout.center.y, layout.radius + 14 * scale, arc.start, arc.end)
        context.stroke()
      }
      if (layout.arcs.length === 0) {
        context.beginPath()
        context.strokeStyle = palette.ringDim
        context.globalAlpha = 0.5
        context.arc(layout.center.x, layout.center.y, layout.radius + 14 * scale, 0, TWO_PI)
        context.stroke()
      }
      context.globalAlpha = 1

      for (const hypothesis of run.hypotheses) {
        const isInvolved = isHighlighting && hypothesis.findingIds.some(id => emphasisedFindings.has(id))
        drawChord(
          hypothesis,
          layout,
          chordStyleFor({
            hypothesis,
            isDimmed: isHighlighting && !isInvolved,
            isInvolved,
            elapsedMs: now - startedAt,
            scale,
            palette,
          })
        )
      }

      for (const [id, point] of layout.points) {
        const isEmphasised = emphasisedFindings.has(id)
        const radius = (size === 'small' ? 2.2 : 3.4) * (isEmphasised ? 1.6 : 1)
        context.beginPath()
        context.arc(point.x, point.y, radius, 0, TWO_PI)
        if (dropped.has(id)) {
          context.strokeStyle = palette.neverIndexed
          context.lineWidth = 1.2
          context.globalAlpha = 0.9
          context.stroke()
        } else {
          context.fillStyle = toolColors[point.toolIndex] ?? palette.ringDim
          context.globalAlpha = hasInputs ? (isHighlighting && !isEmphasised ? 0.35 : 1) : 0.15
          context.fill()
        }
        context.globalAlpha = 1
      }

      const copy = centerCopy(run)
      context.textAlign = 'center'
      context.textBaseline = 'middle'
      context.fillStyle = palette.ink
      context.font = `600 ${size === 'small' ? 22 : 34}px 'DM Mono', monospace`
      context.fillText(copy.headline, layout.center.x, layout.center.y - (copy.headline ? 6 : 0) * scale)
      context.fillStyle = palette.inkMuted
      context.font = `${size === 'small' ? 11 : 14}px 'Source Sans 3', sans-serif`
      context.fillText(copy.caption, layout.center.x, layout.center.y + (size === 'small' ? 14 : 26))

      if (isAnimating) {
        animationFrameId = requestAnimationFrame(draw)
      }
    }

    resize()
    draw(performance.now())
    const resizeObserver = new ResizeObserver(() => {
      resize()
      // While animating, the running rAF loop picks up the new layout on its next frame; drawing
      // here as well would schedule a second loop.
      if (!isAnimating) {
        draw(performance.now())
      }
    })
    resizeObserver.observe(canvas)

    return () => {
      isCanceled = true
      if (animationFrameId !== undefined) {
        cancelAnimationFrame(animationFrameId)
      }
      resizeObserver.disconnect()
    }
  }, [run, size, activeHighlight, isAnimating])

  const updateHover = (findingId: string | undefined) => {
    if (findingId !== hoveredFindingId) {
      setHoveredFindingId(findingId)
      onHighlightFinding?.(findingId)
    }
  }

  const tools = run.tools ?? []
  const hasDropped = run.droppedIds.length > 0

  return (
    <div className={classNames(styles.container, className)}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={label}
        className={styles.canvas[size]}
        onMouseMove={event => updateHover(findingAt(canvasRef.current, layoutRef.current, event))}
        onMouseLeave={() => updateHover(undefined)}
      />
      {size === 'large' && (
        <ul className={styles.legend} aria-label="Canvas legend">
          {tools.map((tool, index) => (
            <li key={tool.tool} className={styles.legendItem}>
              <span className={styles.swatch} style={{ background: toolColorToken(index) }} aria-hidden="true" />
              {toolDisplayName(tool.tool)}
              {kindLabel(tool.kind) ? ` · ${kindLabel(tool.kind)}` : ''} {tool.count}
            </li>
          ))}
          {hasDropped && (
            <li className={styles.legendItem}>
              <span className={styles.swatchHollow} aria-hidden="true" />
              never indexed
            </li>
          )}
          <li className={styles.legendItem}>
            <span className={styles.lineSample.hypothesis} aria-hidden="true" />
            hypothesis
          </li>
          <li className={styles.legendItem}>
            <span className={styles.lineSample.ghost} aria-hidden="true" />
            did not hold
          </li>
          <li className={styles.legendItem}>
            <span className={styles.lineSample.held} aria-hidden="true" />
            held
          </li>
        </ul>
      )}
    </div>
  )
}
